import { memo as Memo, useMemo, useEffect, useRef, useCallback, useState, useLayoutEffect } from 'react';
import { gsap } from 'gsap';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';
import withLanguageContext from '@/context/consumerHOC/LanguageConsumer';

//* Helpers
import breakString from '@helpers/breakString';

//* Components
import Text from '@/components/global/Text';
import SocIcon from '@/components/global/SocIcon';
import CustomLink from '@components/global/CustomLink';
import ZiggyHomeTop from '@components/global/SvgComponents/ZiggyHomeTop';

//* Styles
import HomeTopStyle from './style';

const HomeTop = Memo(({ data, translate, selectedLang, winWidth }) => {
	//! Ref
	const topRef1 = useRef(null);
	const topRef2 = useRef(null);
	const svgRef = useRef(null);
	const textRef = useRef(null);

	const tlMainRef = useRef(null);
	const tlTextRef = useRef(null);

	const prevIdxRef = useRef(1);

	//! State
	const [play, setPlay] = useState(true);

	//! Random Text select
	const getRandomString = useCallback(() => {
		let next = prevIdxRef.current;
		while (next === prevIdxRef.current) {
			next = Math.floor(Math.random() * 3) + 1;
		}
		prevIdxRef.current = next;
		return next;
	}, []);

	useEffect(() => {
		let raf = 0;
		const onScroll = () => {
			if (raf) return;
			raf = requestAnimationFrame(() => {
				raf = 0;
				const el = topRef2.current?.getBoundingClientRect();
				if (el) setPlay(el.y > -el.height && el.y < window.innerHeight);
			});
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		onScroll();
		return () => {
			if (raf) cancelAnimationFrame(raf);
			window.removeEventListener('scroll', onScroll);
		};
	}, []);

	//! Control play pause
	useEffect(() => {
		if (play) {
			tlTextRef.current?.play();
			tlMainRef.current?.play();
		} else {
			tlTextRef.current?.pause();
			tlMainRef.current?.pause();
		}
	}, [play]);

	//! Text anim
	useLayoutEffect(() => {
		const ctx = gsap.context(() => {
			tlMainRef.current?.kill();
			tlTextRef.current?.kill();

			const HOLD_SEC = 0.8;
			const tlText = gsap.timeline({ paused: true, repeat: -1 });

			{
				const idx = getRandomString();
				if (textRef.current) {
					textRef.current.textContent = translate(`startJourneyMiddleTextLine3_${idx}`);
				}
			}

			tlText
				.to(textRef.current, { duration: 0.25, ease: 'power1.inOut' })
				.add(() => {
					const idx = getRandomString();
					if (textRef.current) {
						textRef.current.textContent = translate(`startJourneyMiddleTextLine3_${idx}`);
					}
				})
				.to({}, { duration: HOLD_SEC });

			tlTextRef.current = tlText;

			const tl = gsap.timeline({ paused: true });

			if (topRef1.current) {
				tl.to(Array.from(topRef1.current.children), { duration: 0, color: 'var(--color2)', stagger: 0.1 });
				const second = topRef1.current.childNodes[1];
				if (second) {
					tl.to(second, { duration: 0, css: { fontStyle: 'italic', fontWeight: 300 } }, 0.2);
				}
			}

			if (topRef2.current) {
				const kids = Array.from(topRef2.current.children);
				const targets = winWidth <= 767 ? kids : [...Array.from(kids[0]?.children || []), kids[1]].filter(Boolean);
				tl.to(targets, { duration: 0, delay: 0.1, opacity: 1, color: 'var(--color2)', stagger: 0.1 });
			}

			tl.play();
			tlText.play();

			tlMainRef.current = tl;
		});

		return () => {
			ctx.revert();
		};
	}, [winWidth, selectedLang]);

	const titleCont = useMemo(
		() => (
			<div className={'title'}>
				<Text
					tag={'h1'}
					ref={topRef1}
					className={'h1 font-poppins font-semi-bold'}>
					{breakString(data.title)}
				</Text>
			</div>
		),
		[data]
	);

	const subtitleCont = <Text className={`p-m font-anonymous font-regular subtitle`}>{data.subtitle}</Text>;
	const descriptionCont = <Text className={`size-new font-poppins font-light description`}>{data.description}</Text>;

	const socIconCont = (
		<div className={'social_icon_container'}>
			<SocIcon
				type={2}
				menuIcon
				isSocial
			/>
		</div>
	);

	const animationCont = (
		<div className={'home_animation_element_img'}>
			<ZiggyHomeTop />
		</div>
	);

	const animationTextCont = useMemo(
		() => (
			<CustomLink
				url={'/contact-form/'}
				className={'home_animation_element_text_wrap'}
				ariaLabel={'ready to go beyond the standard product experience? Start your journey here!'}>
				<div className={`home_animation_element_top ${selectedLang === 'de' ? 'home_animation_element_top_ls' : ''}`}>
					<Text className={'font-anonymous font-regular'}>
						<Text
							tag={'span'}
							text={'startJourneyMiddleTextLine1'}
						/>
						<Text
							tag={'span'}
							text={'startJourneyMiddleTextLine2'}
						/>
						<Text tag={'span'}>
							<strong
								ref={textRef}
								className={`font-italic font-bold`}>
								{translate('startJourneyMiddleTextLine3_1')}
							</strong>
							{translate('startJourneyMiddleTextLine3')}
						</Text>
					</Text>
				</div>

				<div className={`home_animation_element_bottom ${selectedLang === 'de' ? 'home_animation_element_bottom_ls' : ''}`}>
					<div
						ref={svgRef}
						className='svg-wrap'>
						<svg
							className={`circle-svg circle_svg`}
							xmlns='http://www.w3.org/2000/svg'
							viewBox='0 0 310 310'>
							<defs>
								<path
									id={`circlePath`}
									className={`circle`}
									d='M2.1,196.1c19.5,48.1,80.4,97.3,153.8,97.3c74.1,0,135.1-46.1,154.2-98.3'
								/>
							</defs>
							<text>
								<textPath
									id='masterTextPath'
									xlinkHref='#circlePath'
									className={`font-anonymous font-bold circle_text`}>
									{translate('startYourJurney')}
								</textPath>
							</text>
						</svg>
					</div>
				</div>
			</CustomLink>
		),
		[selectedLang, translate]
	);

	const infoDesktopCont = (
		<>
			<div className={'top_section_info_wrap'}>
				{subtitleCont}
				{descriptionCont}
				{socIconCont}
			</div>
			<div className={'home_animation_element_wrap'}>
				{animationCont}
				{animationTextCont}
			</div>
		</>
	);

	const infoMobileCont = (
		<>
			{subtitleCont}
			<div className={'home_animation_element_wrap'}>{animationCont}</div>
			{socIconCont}
			{descriptionCont}
			<div className={'home_animation_element_wrap'}>{animationTextCont}</div>
		</>
	);

	return (
		<HomeTopStyle>
			{titleCont}
			<div
				ref={topRef2}
				className={'top_section_wrap'}>
				{winWidth <= 767 ? infoMobileCont : infoDesktopCont}
			</div>
		</HomeTopStyle>
	);
});

export default withUIContext(withLanguageContext(HomeTop, ['translate', 'selectedLang']), ['winWidth']);
